import { upscaleArtwork } from './links';

/** A song from the iTunes catalog, normalised for our UI (browser and server use the same shape). */
export interface CatalogTrack {
  itunesTrackId: number;
  itunesArtistId: number | null;
  title: string;
  artist: string;
  album: string | null;
  itunesGenre: string | null;
  artworkUrl: string | null;
  previewUrl: string | null;
  appleMusicUrl: string | null;
  durationMs: number | null;
  explicit: boolean;
}

export interface CatalogArtist {
  itunesArtistId: number;
  name: string;
  itunesGenre: string | null;
  /** The artist's Apple Music page, so people can check they picked the right one. */
  appleMusicUrl: string | null;
  /**
   * A picture for the artist: the cover of their best-known album. The public iTunes API has no
   * artist photos (D60), so this comes from a lookup of their albums; null when there is none.
   */
  imageUrl: string | null;
}

export const ITUNES_SEARCH_URL = 'https://itunes.apple.com/search';
export const ITUNES_LOOKUP_URL = 'https://itunes.apple.com/lookup';
export const SEARCH_LIMIT = 8;
export const SEARCH_MIN_CHARS = 2;
export const SEARCH_DEBOUNCE_MS = 400;

interface RawItunesItem {
  wrapperType?: string;
  kind?: string;
  trackId?: number;
  artistId?: number;
  trackName?: string;
  artistName?: string;
  collectionName?: string;
  primaryGenreName?: string;
  artworkUrl100?: string;
  previewUrl?: string;
  trackViewUrl?: string;
  trackTimeMillis?: number;
  trackExplicitness?: string;
}

/** Drops iTunes affiliate/tracking parameters, keeping the song (`i`) parameter. */
export function cleanAppleMusicUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (!['music.apple.com', 'itunes.apple.com', 'geo.music.apple.com'].includes(u.hostname)) {
      return null;
    }
    const i = u.searchParams.get('i');
    u.search = i ? `?i=${i}` : '';
    return u.toString();
  } catch {
    return null;
  }
}

function https(url: string | undefined): string | null {
  return url && url.startsWith('https://') ? url : null;
}

export function normalizeItunesTrack(raw: unknown): CatalogTrack | null {
  const item = raw as RawItunesItem;
  if (item?.wrapperType !== 'track' || item.kind !== 'song') return null;
  if (typeof item.trackId !== 'number' || !item.trackName || !item.artistName) return null;
  return {
    itunesTrackId: item.trackId,
    itunesArtistId: typeof item.artistId === 'number' ? item.artistId : null,
    title: item.trackName,
    artist: item.artistName,
    album: item.collectionName ?? null,
    itunesGenre: item.primaryGenreName ?? null,
    artworkUrl: upscaleArtwork(https(item.artworkUrl100)),
    previewUrl: https(item.previewUrl),
    appleMusicUrl: cleanAppleMusicUrl(item.trackViewUrl),
    durationMs: typeof item.trackTimeMillis === 'number' ? item.trackTimeMillis : null,
    explicit: item.trackExplicitness === 'explicit',
  };
}

export function normalizeItunesArtist(raw: unknown): CatalogArtist | null {
  const item = raw as {
    wrapperType?: string;
    artistId?: number;
    artistName?: string;
    primaryGenreName?: string;
    artistLinkUrl?: string;
  };
  if (item?.wrapperType !== 'artist' || typeof item.artistId !== 'number' || !item.artistName)
    return null;
  return {
    itunesArtistId: item.artistId,
    name: item.artistName,
    itunesGenre: item.primaryGenreName ?? null,
    appleMusicUrl: cleanAppleMusicUrl(item.artistLinkUrl),
    imageUrl: null,
  };
}

/** Parses an iTunes search/lookup response into songs (deduplicated, in order). */
export function parseItunesTracks(json: unknown): CatalogTrack[] {
  const results = (json as { results?: unknown[] })?.results ?? [];
  const seen = new Set<number>();
  const out: CatalogTrack[] = [];
  for (const r of results) {
    const t = normalizeItunesTrack(r);
    if (t && !seen.has(t.itunesTrackId)) {
      seen.add(t.itunesTrackId);
      out.push(t);
    }
  }
  return out;
}

export function parseItunesArtists(json: unknown): CatalogArtist[] {
  const results = (json as { results?: unknown[] })?.results ?? [];
  return results.map(normalizeItunesArtist).filter((a): a is CatalogArtist => a !== null);
}

export function itunesSearchUrl(
  term: string,
  entity: 'song' | 'musicArtist',
  country: string,
): string {
  const params = new URLSearchParams({
    term: term.trim(),
    entity,
    limit: String(SEARCH_LIMIT),
    country: country.toUpperCase(),
    media: 'music',
  });
  return `${ITUNES_SEARCH_URL}?${params.toString()}`;
}

export function itunesLookupUrl(id: number, country: string): string {
  const params = new URLSearchParams({
    id: String(id),
    country: country.toUpperCase(),
    entity: 'song',
  });
  return `${ITUNES_LOOKUP_URL}?${params.toString()}`;
}

/** One album per artist, for several artists in a single request (their covers stand in for photos). */
export function itunesArtistAlbumsUrl(ids: number[], country: string): string {
  const params = new URLSearchParams({
    id: ids.join(','),
    entity: 'album',
    limit: '1',
    country: country.toUpperCase(),
  });
  return `${ITUNES_LOOKUP_URL}?${params.toString()}`;
}

/** Artist id → album cover (small, for list avatars), from an `itunesArtistAlbumsUrl` answer. */
export function parseArtistCovers(json: unknown): Map<number, string> {
  const results = (json as { results?: unknown[] })?.results ?? [];
  const covers = new Map<number, string>();
  for (const raw of results) {
    const item = raw as RawItunesItem;
    if (item?.wrapperType !== 'collection' || typeof item.artistId !== 'number') continue;
    const url = upscaleArtwork(https(item.artworkUrl100), 200);
    if (url && !covers.has(item.artistId)) covers.set(item.artistId, url);
  }
  return covers;
}

export function itunesArtistLookupUrl(id: number, country: string): string {
  const params = new URLSearchParams({ id: String(id), country: country.toUpperCase() });
  return `${ITUNES_LOOKUP_URL}?${params.toString()}`;
}

/** Cache/throttle key for a search: case- and whitespace-insensitive. */
export function searchKey(term: string): string {
  return term.trim().toLowerCase().replace(/\s+/g, ' ');
}
